import React, { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import {
  ArrowRightEndOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import ServiceCategoryTooltip from "./ServiceCategoryTooltip";
import { isFoundationAllLabel } from "../utils/foundationScope";
import { getRfiCapabilityBadgeDisplayName } from "../utils/rfiCatalogServiceNames";
import { compareFoundationFirst } from "../utils/serviceCategoryBadgeDisplay";

const CustomBadges = ({
  text,
  cardView,
  badgeColor,
  services,
  isProviderDetail,
  ringColor,
  icon,
}) => {
  const { rfis } = useSelector((state) => state.rfisColors);
  const [serviceColors, setServiceColors] = useState([]);

  const sortedServices = [...services].sort((a, b) => {
    const foundation = compareFoundationFirst(a?.rfi_name, b?.rfi_name);
    if (foundation !== 0) return foundation;
    return a?.rfi_name?.localeCompare(b.rfi_name);
  });

  const mappingRfisColor = () => {
    // Map `services` to match their colors from `rfis`
    const colors = sortedServices?.map((service) => {
      const matchedRfi = rfis?.find((rfi) => rfi.rfi_id === service.rfi_id);
      const displayName = getRfiCapabilityBadgeDisplayName(service.rfi_name);
      return matchedRfi
        ? {
            rfi_id: service.rfi_id,
            bgColor: matchedRfi.rfi_color_code_bg,
            textColor: matchedRfi.rfi_color_code_text,
            borderColor: matchedRfi.rfi_color_code_border,
            name: displayName,
          }
        : {
            rfi_id: service.rfi_id,
            bgColor: "#e0e0e0", // Default color if no match
            textColor: "#000",
            borderColor: "#ccc",
            name: displayName,
          };
    });

    setServiceColors(colors || []);
  };

  useEffect(() => {
    mappingRfisColor();
  }, [services, rfis]);

  return (
    <div className="flex flex-wrap items-center">
      {serviceColors.slice(0, 4).map((service, index) => {
        const isNameSliced =
          !isFoundationAllLabel(service.name) && service.name?.length > 14;
        const displayName = isNameSliced
          ? `${service.name.slice(0, 14)}...`
          : service.name;

        const badge = (
          <span
            key={service.rfi_id || index}
            style={{
              backgroundColor: service.bgColor,
              boxShadow: `inset 0 0 0 1px ${service.borderColor}1A`, // Simulates ring-*/* effect (10% opacity)
              color: service.textColor,
            }}
            className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium mr-2 mb-1`}
          >
            {displayName}
            {icon && <ServiceCategoryTooltip name={service.name} />}
          </span>
        );

        return (
          <Tooltip
            key={index}
            title={
              isNameSliced ? (
                <span
                  style={{
                    backgroundColor: service.bgColor,
                    color: service.textColor,
                    borderColor: service.borderColor,
                  }}
                  className={`inline-flex items-center rounded px-2 py-1 text-xs font-medium`}
                >
                  {service.name}
                  {icon && <ServiceCategoryTooltip name={service.name} />}
                </span>
              ) : null
            }
            arrow
            placement="bottom"
            PopperProps={{
              style: {
                backgroundColor: "white",
                color: "white",
              },
            }}
            classes={{
              tooltip: "custom-tooltip",
            }}
          >
            {badge}
          </Tooltip>
        );
      })}

      {serviceColors.length > 4 && (
        <Tooltip
          title={
            <div className="p-2 text-black rounded">
              <h1 className="text-base mb-2 text-custom-link-bg-color">
                All Services
              </h1>
              <div className="flex flex-wrap gap-2">
                {serviceColors.map((service, index) => (
                  <span
                    key={service.rfi_id || index}
                    style={{
                      backgroundColor: service.bgColor,
                      boxShadow: `inset 0 0 0 1px ${service.borderColor}1A`, // Simulates ring-*/* effect (10% opacity)
                      color: service.textColor,
                    }}
                    className="inline-flex items-center rounded px-2 py-1 text-xs font-medium space-x-2"
                  >
                    {service.name}
                    {icon && <ServiceCategoryTooltip name={service.name} />}
                  </span>
                ))}
              </div>
            </div>
          }
          arrow
          placement="bottom"
          PopperProps={{
            style: {
              backgroundColor: "white",
            },
          }}
          classes={{
            tooltip: "custom-tooltip",
          }}
        >
          <span className="flex items-center rounded text-xs font-thin cursor-pointer">
            <ArrowRightEndOnRectangleIcon
              className="w-4 h-4"
              style={{ color: "#AB54DB" }}
            />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default CustomBadges;
